import { useEffect, forwardRef, useImperativeHandle, useRef, useState, useContext } from "react";
import { fabric } from 'fabric';
import { AIEditContext } from "../../AIEditContext";
import { useDrawRect } from './hooks/useDrawRect'
import useCircleSelection from './hooks/useCircleSelection';
import usePaintEraser from './hooks/usePaintEraser'
import styles from './index.module.scss'
import { MODESTATE, OPERATETYPE } from '@view/page_v3/AiEdit/CONST'
import { CloseOutlined } from '@ant-design/icons';

const formartNumber = (number, digits = 2) => {
    return Number(number.toFixed(digits))
}


const HandRepairCanvas = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        clear,
        getDataURL,
        getSaveData,
        undo,
        loadSaveData,
    }));

    const { addWatermark, operateType, modeState } = useContext(AIEditContext)

    const operateTypeRef = useRef()
    const modeStateRef = useRef()

    const bgCanvasRef = useRef()
    const bgInstanceRef = useRef()

    const canvasRef = useRef()
    const fabricInstanceRef = useRef()
    const [fabricInstance, setFabricInstance] = useState()

    const pencilBrushRef = useRef()
    const circleMouseRef = useRef()

    const moveableRef = useRef(props.moveable)
    const ctrlPressedRef = useRef(false)
    const displayScaleRef = useRef(props.displayScale)

    const sizeRef = useRef()

    useEffect(() => {
        sizeRef.current = props.size
    }, [props.size])



    useEffect(() => {
        ctrlPressedRef.current = props.ctrlKeyPressed
    }, [props.ctrlKeyPressed])

    useEffect(() => {
        displayScaleRef.current = props.displayScale
    }, [props.displayScale])

    useEffect(() => {
        moveableRef.current = props.moveable
    }, [props.moveable])

    const isUndoableType = (item) => {
        return ['path', 'rect', 'circle'].includes(item.type);
    };

    const getDataURL = () => {
        // console.log('getDataURL')
        const pathList = fabricInstanceRef.current.getObjects().filter(item => isUndoableType(item))
        const tempCanvas = new fabric.StaticCanvas(null, {
            width: fabricInstanceRef.current.width,
            height: fabricInstanceRef.current.height,
            backgroundColor: '#000'
        })
        tempCanvas.add(...pathList)
        const exportPath = tempCanvas.toDataURL({ format: 'png', multiplier: 1 / displayScaleRef.current })
        return exportPath
    }
    const getSaveData = () => {
        const bgSaveData = bgInstanceRef.current.toJSON()
        const saveData = fabricInstanceRef.current.toJSON()
        return {
            bgjson: bgSaveData,
            json: saveData,
            lines: saveData.objects.filter(item => isUndoableType(item))
        }
    }
    const undo = () => {
        const pathList = fabricInstanceRef.current.getObjects().filter(item => isUndoableType(item))
        if (pathList.length > 0) {
            fabricInstanceRef.current.remove(pathList.pop())
        }
        props.getIsDraw(pathList)
    }
    const loadSaveData = ({ bgjson, json, lines }) => {
        // 只加载前景画布的绘制内容，避免重复加载背景
        fabricInstanceRef.current.loadFromJSON(json, () => {
            fabricInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight });

            const objs = fabricInstanceRef.current.getObjects();
            let allObj = objs.filter(item => item.type == 'rect' || item.type == 'path')
            let allObj2 = objs.filter(item => item.strokeLineCap == 'round') // 这是涂抹路径区别与圈选路径的属性
            if (allObj.length > 0) {
                allObj.map((item) => {
                    item.set({
                        hasControls: true,
                        selectable: true,
                        evented: true,
                        cornerSize: 8,
                    });
                    item.setControlsVisibility({
                        mtr: false,
                    });
                })
            }
            if (allObj2.length > 0) {
                allObj2.map((item) => {
                    item.set({
                        hasControls: false,
                        selectable: false,
                        evented: false,
                    });
                })
            }
            fabricInstanceRef.current.renderAll();
        });
        props.getIsDraw(lines);
    };

    const clear = () => {
        // console.log('clear')
        pencilBrushRef.current._reset() // 清除path
        const pathList = fabricInstanceRef.current.getObjects().filter(item => isUndoableType(item))
        fabricInstanceRef.current.remove(...pathList)
    }
    const patternImageBase64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAABHNCSVQICAgIfAhkiAAAADBJREFUOE9jfPP69X8GPEBYRASfNAPjqAHDIgz+AwG+iH7z5s1ZfPKMowYwDIMwAACuR1xZvSaHFwAAAABJRU5ErkJggg==';
    const pattern = new fabric.Pattern({
        source: patternImageBase64,
        repeat: 'repeat',
    })
    useEffect(() => {
        fabric.Image.fromURL(props.url, async (img) => {
            img.scale(props.displayScale)
            const bgInstance = new fabric.Canvas(bgCanvasRef.current, {
                width: props.displayWidth,
                height: props.displayHeight,
                backgroundColor: pattern, // 加载马赛克背景
                backgroundImage: img, // 加载默认背景
                fireRightClick: false, // 启用右键
                stopContextMenu: true, // 禁用右键菜单
                isDrawingMode: false, // 禁用绘图模式
                // selection: false, // 禁用选中对象
            })
            await addWatermark(bgInstance) // 添加水印
            bgInstanceRef.current = bgInstance

            const instance = new fabric.Canvas(canvasRef.current, {
                width: props.displayWidth,
                height: props.displayHeight,
                fireRightClick: false, // 启用右键
                stopContextMenu: true, // 禁用右键菜单
                isDrawingMode: false, // 禁用绘图模式
                // selection: false, // 禁用选中对象
            })
            instance.includeDefaultValues = false

            fabricInstanceRef.current = instance
            setFabricInstance(instance)
            props.setFabricInstance(bgInstance, instance)
        }, { crossOrigin: 'anonymous' })
    }, [])



    useEffect(() => {
        if (!bgInstanceRef.current) return
        const bgImage = bgInstanceRef.current.get('backgroundImage')
        bgImage.setSrc(props.url, () => bgInstanceRef.current.renderAll(), { crossOrigin: 'anonymous' })
    }, [props.url])

    useEffect(() => {
        if (!pencilBrushRef.current || !fabricInstanceRef.current || !circleMouseRef.current) return
        if (!fabricInstanceRef.current) return
        pencilBrushRef.current.width = props.size
        circleMouseRef.current.set({ radius: props.size / 2 })
        fabricInstanceRef.current.renderAll()
    }, [props.size, props.displayScale])

    useEffect(() => { // 控制是否启用绘图模式
        if (!fabricInstanceRef.current) return
        if (props.moveable || operateTypeRef.current != OPERATETYPE.DRAW) {
            fabricInstanceRef.current.isDrawingMode = false
        } else {
            fabricInstanceRef.current.isDrawingMode = true
        }
    }, [props.moveable, operateTypeRef.current])

    const drawingRef = useRef()
    useEffect(() => {
        if (!fabricInstance) return

        fabricInstance.on('mouse:down', (event) => {
            if (moveableRef.current) return
            if (operateTypeRef.current === OPERATETYPE.DRAW) {
                drawingRef.current = true;
            } else if (operateTypeRef.current === OPERATETYPE.RECT) {
                drawingRef.current = true;
                startRect(event);
            } else if (operateTypeRef.current === OPERATETYPE.CIRCLE) {
                const { x, y } = fabricInstanceRef.current.getPointer(event.e);
                points.push([x, y]);  // 记录起始点
                fabricInstanceRef.current.selection = false; // 禁止出现默认的矩形框
                drawingRef.current = true;
            }
        })
        fabricInstance.on('mouse:move', (event) => {
            if (operateTypeRef.current === OPERATETYPE.RECT && drawingRef.current) {
                moveRect(event);
            }
            if (operateTypeRef.current === OPERATETYPE.DRAW) {
                updatePathPosition(event);
                if (modeStateRef.current == MODESTATE.ERASER) updatePathWhenEarse(event)
            }
            if (operateTypeRef.current === OPERATETYPE.CIRCLE) {
                updateCirclePosition(event);
            }
        })
        fabricInstance.on('mouse:up', () => {
            if (operateTypeRef.current === OPERATETYPE.DRAW) {
                drawingRef.current = false;
                mouseUpWhenEarse()
            } else if (operateTypeRef.current === OPERATETYPE.RECT && drawingRef.current) {
                endRect();
                drawingRef.current = false;
            } else if (operateTypeRef.current === OPERATETYPE.CIRCLE && drawingRef.current) {
                endCircle();
                drawingRef.current = false;
            }
        })
        fabricInstance.on('path:created', ({ path }) => {
            path.set({ selectable: false })
            const pathList = fabricInstanceRef.current.getObjects().filter(item => item.type == 'path')
            props.getIsDraw(pathList)
        })
        fabricInstance.on('mouse:wheel', function (event) {
            if (!circleMouseRef.current) return
            if (!ctrlPressedRef.current) return

            event.e.preventDefault();
            event.e.stopPropagation();

            const delta = event.e.deltaY;
            // 根据滚轮滚动方向调整缩放比例
            if (delta > 0) {
                props.changeBrushSize(-1)
            } else {
                props.changeBrushSize(1)
            }
        })

        // 监听鼠标移入事件，显示圆形画笔----------------处理涂抹移出画布情况
        fabricInstance.on('mouse:over', () => {
            if (!circleMouseRef.current) return;

            let circleRef = null;
            if (Array.isArray(circleMouseRef.current)) {
                circleRef = circleMouseRef.current.find((item) => item.type == 'circle');
            } else {
                circleRef = circleMouseRef.current;
            }

            if (circleRef && !fabricInstance.contains(circleRef) && operateTypeRef.current === OPERATETYPE.DRAW) {
                fabricInstance.add(circleRef);
                fabricInstance.bringToFront(circleRef);
            }
        });

        // 监听鼠标移出事件，隐藏圆形画笔
        fabricInstance.on('mouse:out', () => {
            // fabricInstanceRef.current.discardActiveObject(); // 鼠标移出矩形或者圈选对象，取消选中
            // fabricInstanceRef.current.renderAll();

            if (!circleMouseRef.current) return;
            let circleRef = null;
            if (Array.isArray(circleMouseRef.current)) {
                circleRef = circleMouseRef.current.find((item) => item.type == 'circle');
            } else {
                circleRef = circleMouseRef.current;
            }
            if (fabricInstance.contains(circleRef)) {
                fabricInstance.remove(circleRef);
            }
        });

    }, [fabricInstance])

    // 组件卸载
    useEffect(() => {
        return () => {
            if (fabricInstanceRef.current) {
                fabricInstanceRef.current.off('mouse:down');
                fabricInstanceRef.current.off('mouse:move');
                fabricInstanceRef.current.off('mouse:up');
            }
        };
    }, []);

    useEffect(() => {
        if (!fabricInstanceRef.current) return
        operateTypeRef.current = operateType
        if (fabricInstanceRef.current) {
            if (operateTypeRef.current === OPERATETYPE.DRAW) {
                fabricInstanceRef.current.isDrawingMode = true
            } else if (operateTypeRef.current === OPERATETYPE.RECT) {
                fabricInstanceRef.current.defaultCursor = 'crosshair';
                fabricInstanceRef.current.isDrawingMode = false
            } else if (operateTypeRef.current === OPERATETYPE.CIRCLE) {
                fabricInstanceRef.current.defaultCursor = 'crosshair';
                fabricInstanceRef.current.isDrawingMode = false;
            }
        }
    }, [operateType, fabricInstanceRef.current])

    useEffect(() => {
        modeStateRef.current = modeState
    }, [modeState])

    useEffect(() => {
        if (!fabricInstanceRef.current) return

        const canvasScaleX = props.displayWidth / fabricInstanceRef.current.width
        const canvasScaleY = props.displayHeight / fabricInstanceRef.current.height

        const bgImage = bgInstanceRef.current.get('backgroundImage')
        if (bgImage) bgImage.scale(props.displayScale)

        bgInstanceRef.current.viewportTransform[4] = bgInstanceRef.current.viewportTransform[4] * canvasScaleX
        bgInstanceRef.current.viewportTransform[5] = bgInstanceRef.current.viewportTransform[5] * canvasScaleY
        bgInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight })

        const objs1 = bgInstanceRef.current.getObjects()
        objs1.forEach(item => {
            item.set({
                scaleX: item.scaleX * canvasScaleX,
                scaleY: item.scaleY * canvasScaleY
            })
            item.setCoords()
        })

        fabricInstanceRef.current.viewportTransform[4] = fabricInstanceRef.current.viewportTransform[4] * canvasScaleX
        fabricInstanceRef.current.viewportTransform[5] = fabricInstanceRef.current.viewportTransform[5] * canvasScaleY
        fabricInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight })

        const objs = fabricInstanceRef.current.getObjects()
        objs.forEach(item => {
            if (item.type == 'path') {
                item.scale(item.scaleX * canvasScaleX)
                item.set({
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
                item.setCoords()
            } else {
                item.set({
                    width: formartNumber(item.width * canvasScaleX),
                    height: formartNumber(item.height * canvasScaleX),
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
                item.setCoords()
            }
        })

        bgInstanceRef.current.renderAll()
        fabricInstanceRef.current.renderAll()
    }, [props.displayWidth, props.displayHeight, props.displayScale])

    const { startRect, moveRect, endRect } = useDrawRect(
        fabricInstanceRef,
        drawingRef,
        props.getIsDraw,
        modeStateRef
    )

    const points = []
    const {
        updateCirclePosition,
        endCircle,
        clearTemporaryPaths,
        pointsRef
    } = useCircleSelection(
        fabricInstanceRef,
        operateTypeRef,
        drawingRef,
        props.getIsDraw,
        points,
        modeStateRef
    )

    const {
        updatePathPosition,
        updatePathWhenEarse,
        mouseUpWhenEarse
    } = usePaintEraser({
        fabricInstanceRef,
        modeStateRef,
        modeState,
        props,
        pencilBrushRef,
        circleMouseRef,
        sizeRef,
        operateTypeRef
    })

    return (
        <div className={styles.magicErase_canvasBox}>
            <div className={styles.magicErase_bgcanvas}><canvas ref={bgCanvasRef} /></div>
            <div className={styles.magicErase_canvas}><canvas ref={canvasRef} /></div>
            {
                <CloseOutlined
                    onClick={props.handleCleanClick}
                    style={{
                        position: 'absolute',
                        right: '-20px',
                        top: 0,
                        cursor: 'pointer'
                    }}
                />
            }
        </div>
    )
})

export default HandRepairCanvas